import React, { useState, useEffect } from 'react';
import { leadAPI, employeeAPI, roleAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LeadDetails from './LeadDetails';
import './LeadManagement.css';

const LeadManagement = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('all-leads');
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showLeadDetails, setShowLeadDetails] = useState(false);
  const [viewingLead, setViewingLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    source: '',
    salesStage: '',
    priority: '',
    search: '',
  });

  // Form state for new lead
  const [formData, setFormData] = useState({
    customerName: '',
    mobile: '',
    email: '',
    alternatePhone: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: '',
    },
    leadSource: 'Walk-in',
    leadStatus: 'New',
    salesStage: 'Inquiry',
    dealValue: 0,
    probability: 0,
    productInterest: '',
    requirement: '',
    budget: { min: 0, max: 0 },
    timeline: 'Within 1 Month',
    priority: 'Medium',
  });

  // Assignment state
  const [assignmentData, setAssignmentData] = useState({
    selectedRole: '',
    selectedEmployee: '',
  });

  useEffect(() => {
    fetchLeads();
    fetchStats();
    fetchRoles();
  }, [filters]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await leadAPI.getAll(filters);
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      alert('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await leadAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await roleAPI.getAll();
      setRoles(response.data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchEmployeesByRole = async (roleId) => {
    try {
      console.log('Fetching employees for role:', roleId);
      const response = await employeeAPI.getAll({ role: roleId });
      console.log('Employee API response:', response);
      console.log('Employees data:', response.data);
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (selectedLead) {
        await leadAPI.update(selectedLead._id, formData);
        alert('Lead updated successfully!');
      } else {
        await leadAPI.create(formData);
        alert('Lead created successfully!');
      }
      resetForm();
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error saving lead:', error);
      alert(error.message || 'Failed to save lead');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await leadAPI.delete(id);
      alert('Lead deleted successfully!');
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error deleting lead:', error);
      alert('Failed to delete lead');
    }
  };

  const handleEdit = (lead) => {
    setSelectedLead(lead);
    setFormData({
      customerName: lead.customerName || '',
      mobile: lead.mobile || '',
      email: lead.email || '',
      alternatePhone: lead.alternatePhone || '',
      address: lead.address || { street: '', city: '', state: '', pincode: '' },
      leadSource: lead.leadSource || 'Walk-in',
      leadStatus: lead.leadStatus || 'New',
      salesStage: lead.salesStage || 'Inquiry',
      dealValue: lead.dealValue || 0,
      probability: lead.probability || 0,
      productInterest: lead.productInterest || '',
      requirement: lead.requirement || '',
      budget: lead.budget || { min: 0, max: 0 },
      timeline: lead.timeline || 'Within 1 Month',
      priority: lead.priority || 'Medium',
    });
    setShowModal(true);
  };

  const handleRoleChange = async (roleId) => {
    setAssignmentData({ selectedRole: roleId, selectedEmployee: '' });
    if (roleId) {
      await fetchEmployeesByRole(roleId);
    } else {
      setEmployees([]);
    }
  };

  const handleAssignLead = async () => {
    if (!assignmentData.selectedEmployee) {
      alert('Please select an employee');
      return;
    }

    try {
      if (selectedLeads.length > 1) {
        await leadAPI.bulkAssign(selectedLeads, assignmentData.selectedEmployee);
        alert(`${selectedLeads.length} leads assigned successfully!`);
      } else {
        await leadAPI.assign(selectedLeads[0], assignmentData.selectedEmployee);
        alert('Lead assigned successfully!');
      }
      setShowAssignModal(false);
      setSelectedLeads([]);
      setAssignmentData({ selectedRole: '', selectedEmployee: '' });
      fetchLeads();
    } catch (error) {
      console.error('Error assigning lead:', error);
      alert('Failed to assign lead');
    }
  };

  const handleCheckboxChange = (leadId) => {
    setSelectedLeads(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await leadAPI.updateStatus(leadId, newStatus);
      alert('Lead status updated successfully!');
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      customerName: '',
      mobile: '',
      email: '',
      alternatePhone: '',
      address: { street: '', city: '', state: '', pincode: '' },
      leadSource: 'Walk-in',
      leadStatus: 'New',
      salesStage: 'Inquiry',
      dealValue: 0,
      probability: 0,
      productInterest: '',
      requirement: '',
      budget: { min: 0, max: 0 },
      timeline: 'Within 1 Month',
      priority: 'Medium',
    });
    setSelectedLead(null);
    setShowModal(false);
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      'New': 'badge-new',
      'Follow-up': 'badge-followup',
      'Hot': 'badge-hot',
      'Cold': 'badge-cold',
      'Converted': 'badge-converted',
      'Lost': 'badge-lost',
      'Dead': 'badge-dead',
    };
    return `status-badge ${statusClasses[status] || 'badge-default'}`;
  };

  const getStageBadgeClass = (stage) => {
    const stageClasses = {
      'Inquiry': 'stage-inquiry',
      'Demo': 'stage-demo',
      'Negotiation': 'stage-negotiation',
      'Booking': 'stage-booking',
      'Closed': 'stage-closed',
      'Lost': 'stage-lost',
    };
    return `stage-badge ${stageClasses[stage] || 'stage-default'}`;
  };

  return (
    <div className="lead-management">
      <div className="page-header">
        <h1>Lead Management</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Add Lead
          </button>
          {selectedLeads.length > 0 && (
            <button 
              className="btn btn-success" 
              onClick={() => setShowAssignModal(true)}
            >
              Assign Selected ({selectedLeads.length})
            </button>
          )}
        </div>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-details">
              <h3>{stats.summary.totalLeads}</h3>
              <p>Total Leads</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🆕</div>
            <div className="stat-details">
              <h3>{stats.summary.newLeads}</h3>
              <p>New Leads</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🔥</div>
            <div className="stat-details">
              <h3>{stats.summary.hotLeads}</h3>
              <p>Hot Leads</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-details">
              <h3>{stats.summary.convertedLeads}</h3>
              <p>Converted</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📈</div>
            <div className="stat-details">
              <h3>{stats.summary.conversionRate}</h3>
              <p>Conversion Rate</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <input
          type="text"
          placeholder="Search by name, email, or mobile..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="search-input"
        />
        <select 
          value={filters.status} 
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">All Status</option>
          <option value="New">New</option>
          <option value="Follow-up">Follow-up</option>
          <option value="Hot">Hot</option>
          <option value="Cold">Cold</option>
          <option value="Converted">Converted</option>
          <option value="Lost">Lost</option>
        </select>
        <select 
          value={filters.source} 
          onChange={(e) => setFilters({ ...filters, source: e.target.value })}
        >
          <option value="">All Sources</option>
          <option value="Website">Website</option>
          <option value="WhatsApp">WhatsApp</option>
          <option value="Facebook">Facebook</option>
          <option value="Instagram">Instagram</option>
          <option value="Call">Call</option>
          <option value="Walk-in">Walk-in</option>
          <option value="Referral">Referral</option>
        </select>
        <select 
          value={filters.salesStage} 
          onChange={(e) => setFilters({ ...filters, salesStage: e.target.value })}
        >
          <option value="">All Stages</option>
          <option value="Inquiry">Inquiry</option>
          <option value="Demo">Demo</option>
          <option value="Negotiation">Negotiation</option>
          <option value="Booking">Booking</option>
          <option value="Closed">Closed</option>
        </select>
      </div>

      {/* Leads Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading">Loading leads...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedLeads(leads.map(l => l._id));
                      } else {
                        setSelectedLeads([]);
                      }
                    }}
                    checked={selectedLeads.length === leads.length && leads.length > 0}
                  />
                </th>
                <th>Lead ID</th>
                <th>Customer Name</th>
                <th>Mobile</th>
                <th>Source</th>
                <th>Status</th>
                <th>Stage</th>
                <th>Deal Value</th>
                <th>Assigned To</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan="10" className="no-data">No leads found</td>
                </tr>
              ) : (
                leads.map(lead => (
                  <tr key={lead._id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead._id)}
                        onChange={() => handleCheckboxChange(lead._id)}
                      />
                    </td>
                    <td>{lead.leadId}</td>
                    <td>{lead.customerName}</td>
                    <td>{lead.mobile}</td>
                    <td>{lead.leadSource}</td>
                    <td>
                      <select
                        value={lead.leadStatus}
                        onChange={(e) => handleStatusChange(lead._id, e.target.value)}
                        className={getStatusBadgeClass(lead.leadStatus)}
                      >
                        <option value="New">New</option>
                        <option value="Follow-up">Follow-up</option>
                        <option value="Hot">Hot</option>
                        <option value="Cold">Cold</option>
                        <option value="Converted">Converted</option>
                        <option value="Lost">Lost</option>
                      </select>
                    </td>
                    <td>
                      <span className={getStageBadgeClass(lead.salesStage)}>
                        {lead.salesStage}
                      </span>
                    </td>
                    <td>₹{lead.dealValue?.toLocaleString() || 0}</td>
                    <td>
                      {lead.assignedTo ? (
                        <span className="assigned-employee">
                          {lead.assignedTo.name}
                        </span>
                      ) : (
                        <span className="unassigned">Unassigned</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn-icon btn-view" 
                          onClick={() => {
                            setViewingLead(lead);
                            setShowLeadDetails(true);
                          }}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <button 
                          className="btn-icon btn-edit" 
                          onClick={() => handleEdit(lead)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button 
                          className="btn-icon btn-assign" 
                          onClick={() => {
                            setSelectedLeads([lead._id]);
                            setShowAssignModal(true);
                          }}
                          title="Assign"
                        >
                          👤
                        </button>
                        <button 
                          className="btn-icon btn-delete" 
                          onClick={() => handleDelete(lead._id)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Lead Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedLead ? 'Edit Lead' : 'Add New Lead'}</h2>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="lead-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>Customer Name *</label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Mobile *</label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Alternate Phone</label>
                  <input
                    type="tel"
                    name="alternatePhone"
                    value={formData.alternatePhone}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Lead Source *</label>
                  <select
                    name="leadSource"
                    value={formData.leadSource}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Website">Website</option>
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Call">Call</option>
                    <option value="Walk-in">Walk-in</option>
                    <option value="Referral">Referral</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Lead Status</label>
                  <select
                    name="leadStatus"
                    value={formData.leadStatus}
                    onChange={handleInputChange}
                  >
                    <option value="New">New</option>
                    <option value="Follow-up">Follow-up</option>
                    <option value="Hot">Hot</option>
                    <option value="Cold">Cold</option>
                    <option value="Converted">Converted</option>
                    <option value="Lost">Lost</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Sales Stage</label>
                  <select
                    name="salesStage"
                    value={formData.salesStage}
                    onChange={handleInputChange}
                  >
                    <option value="Inquiry">Inquiry</option>
                    <option value="Demo">Demo</option>
                    <option value="Negotiation">Negotiation</option>
                    <option value="Booking">Booking</option>
                    <option value="Closed">Closed</option>
                    <option value="Lost">Lost</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Deal Value (₹)</label>
                  <input
                    type="number"
                    name="dealValue"
                    value={formData.dealValue}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Probability (%)</label>
                  <input
                    type="number"
                    name="probability"
                    value={formData.probability}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                  />
                </div>
                <div className="form-group">
                  <label>Timeline</label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                  >
                    <option value="Immediate">Immediate</option>
                    <option value="Within 1 Month">Within 1 Month</option>
                    <option value="1-3 Months">1-3 Months</option>
                    <option value="3-6 Months">3-6 Months</option>
                    <option value="More than 6 Months">More than 6 Months</option>
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>Product Interest</label>
                  <input
                    type="text"
                    name="productInterest"
                    value={formData.productInterest}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Requirement</label>
                  <textarea
                    name="requirement"
                    value={formData.requirement}
                    onChange={handleInputChange}
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label>Street Address</label>
                  <input
                    type="text"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="address.state"
                    value={formData.address.state}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Pincode</label>
                  <input
                    type="text"
                    name="address.pincode"
                    value={formData.address.pincode}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : selectedLead ? 'Update Lead' : 'Create Lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Lead Modal */}
      {showAssignModal && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content assign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Lead{selectedLeads.length > 1 ? 's' : ''}</h2>
              <button className="close-btn" onClick={() => setShowAssignModal(false)}>×</button>
            </div>
            <div className="assign-form">
              <p className="assign-info">
                Assigning {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''}
              </p>
              <div className="form-group">
                <label>Select Role</label>
                <select
                  value={assignmentData.selectedRole}
                  onChange={(e) => handleRoleChange(e.target.value)}
                >
                  <option value="">-- Select Role --</option>
                  {roles.map(role => (
                    <option key={role._id} value={role._id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
              {assignmentData.selectedRole && (
                <div className="form-group">
                  <label>Select Employee</label>
                  <select
                    value={assignmentData.selectedEmployee}
                    onChange={(e) => setAssignmentData({ ...assignmentData, selectedEmployee: e.target.value })}
                  >
                    <option value="">-- Select Employee --</option>
                    {employees.map(emp => (
                      <option key={emp._id} value={emp._id}>
                        {emp.name} - {emp.employeeId} ({emp.designation})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="modal-footer">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowAssignModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleAssignLead}
                  disabled={!assignmentData.selectedEmployee}
                >
                  Assign Lead{selectedLeads.length > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lead Details Modal */}
      {showLeadDetails && viewingLead && (
        <LeadDetails
          lead={viewingLead}
          onClose={() => {
            setShowLeadDetails(false);
            setViewingLead(null);
          }}
          onUpdate={fetchLeads}
        />
      )}
    </div>
  );
};

export default LeadManagement;
